import { useCallback, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { useFetchCallback } from '@services/services';
import { Order } from '@interfaces/OrderTypes';
import { MSG_LIST_ERROR } from '@pages/Orders/messages';
import { SOCKET_EVENTS } from '@utils/constants';
import { useGetCommerce } from '@pages/Commerce/hooks/commerce';
import { useOrderActions, useOrdersFromStore } from '@store/actions/orders';
import { useOrderWithDetailsActions } from '@store/actions/ordersWithDetails';
import useModules from './useModules';
import { useWebSocket } from './useWebSocket';
import { hasPendingSelfServiceHeal, selectOrdersToReconcile, selectSelfServiceToHeal } from './reconcileActiveOrders';

// Backstop de agrupamiento de mesa (T4): debounce trailing de la reconciliación disparada por
// actividad WS. Un evento entrante puede significar que una asociación de mesa recién se materializó
// en el backend; re-consultamos, pero COLAPSANDO ráfagas en una sola llamada tras 12s de silencio
// (no en cada evento) para no pollear en idle ni machacar el backend.
const SELF_SERVICE_BACKSTOP_DEBOUNCE_MS = 12000;
// Techo del debounce: bajo tráfico WS sostenido (eventos con <12s de separación) el trailing puro
// nunca llegaría a expirar y el sanado se moriría de hambre justo en hora pica. maxWait fuerza el
// reconcile a lo más cada 18s aunque los eventos sigan llegando.
const SELF_SERVICE_BACKSTOP_MAX_WAIT_MS = 18000;

// Reconciliación del Kanban "Activos" — red de seguridad para el push WS perdido (CS-139/CS-140).
//
// El push `newOrder` es fire-and-forget: si el socket está caído/idle/stale cuando el backend activa
// un pedido (p.ej. un programado vía `auto_fire` a una hora arbitraria), el pedido NUNCA llega al
// Kanban hasta recargar. Este hook re-trae los pedidos activos y los MERGEA de forma idempotente:
//
//   1. Al (re)conectar el socket — reacciona a `connectionEpoch` del WebSocketContext, que se
//      incrementa en cada `onopen` (alta inicial Y cada reconexión). Inyecta lo activado mientras
//      el socket estaba caído.
//   2. Al recuperar foco/visibilidad — `window` focus y `visibilitychange → visible`. Cubre el caso
//      real del reporte: laptop dormida / pestaña en background cuando se dispara el auto_fire.
//
// El merge usa `addOrder` (dedup por `id` en el reducer) sobre las filas que `selectOrdersToReconcile`
// marca como activas y ausentes del store → sin duplicados, sin parpadeo, sin inyectar programados
// que aún no se activan. Se monta una sola vez (en PrivateGlobalHooks), junto a useIncomingOrder.
export const useReconcileActiveOrders = () => {
  const { connectionEpoch, subscribeEvent, unsubscribeEvent } = useWebSocket();
  const { commerce } = useGetCommerce();
  const orders = useOrdersFromStore();
  const { addOrder, mergeSelfService } = useOrderActions();
  const { mergeSelfService: mergeSelfServiceWithDetails } = useOrderWithDetailsActions();
  const { hasModule } = useModules();
  const { call: fetchOrders } = useFetchCallback<null, { count: number; rows: Order[] }>('get', '/v1/pedido');

  // Refs para que los listeners (registrados una vez) lean SIEMPRE el valor más reciente sin
  // re-suscribirse en cada render (mismo patrón que useIncomingOrder con accountCommerceTokensRef).
  const ordersRef = useRef(orders);
  ordersRef.current = orders;
  const commerceIdRef = useRef(commerce?.id);
  commerceIdRef.current = commerce?.id;
  // `fetchOrders`/`addOrder`/`mergeSelfService`/`mergeSelfServiceWithDetails`/`hasModule` obtienen
  // referencia nueva en cada render (useFetchCallback, useOrderActions y el useMemo de useModules no
  // están memoizados de forma estable). Se leen vía ref para que `reconcile` (useCallback con deps [])
  // sea de verdad estable y no re-registre los listeners WS en cada render.
  const fetchOrdersRef = useRef(fetchOrders);
  fetchOrdersRef.current = fetchOrders;
  const addOrderRef = useRef(addOrder);
  addOrderRef.current = addOrder;
  const mergeSelfServiceRef = useRef(mergeSelfService);
  mergeSelfServiceRef.current = mergeSelfService;
  const mergeSelfServiceWithDetailsRef = useRef(mergeSelfServiceWithDetails);
  mergeSelfServiceWithDetailsRef.current = mergeSelfServiceWithDetails;
  const hasModuleRef = useRef(hasModule);
  hasModuleRef.current = hasModule;
  // Evita reconciliaciones solapadas (varios triggers casi simultáneos: reconexión + focus).
  const inFlightRef = useRef(false);

  // `silentError`: los disparos por foco/visibilidad/reconexión SÍ muestran el toast de error de
  // carga (el operador acaba de volver y espera ver el listado); el disparo por actividad WS (T4)
  // corre en segundo plano cada tanto → NO debe spamear un toast de error si el endpoint tiene un
  // blip de red.
  const reconcile = useCallback(async ({ silentError = false }: { silentError?: boolean } = {}) => {
    if (!commerceIdRef.current) return; // sin comercio activo no hay Kanban que reconciliar
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const response = await fetchOrdersRef.current({ path: '?orders_in_process=true' });
      if (!response?.rows) return;
      const toAdd = selectOrdersToReconcile(response.rows, ordersRef.current);
      // addOrder vuelve a deduplicar por id en el reducer: doble red contra duplicados/parpadeo.
      toAdd.forEach((order) => addOrderRef.current(order));
      // T4 · backstop de agrupamiento: sana los pedidos de mesa YA presentes en el store pero
      // sueltos (self_service null) cuya fila fetched ya trae la asociación. Mergea en AMBOS slices
      // (orders y ordersWithDetails), igual que el healer T3 — si solo tocara `orders`, el detalle
      // cacheado seguiría con self_service:null (número de mesa ausente al abrir el detalle /
      // reimprimir). Idempotente y anti des-agrupar. Se calcula sobre el store previo al addOrder:
      // los recién inyectados ya traen su self_service intacto del fetch.
      const heals = selectSelfServiceToHeal(response.rows, ordersRef.current);
      heals.forEach((heal) => {
        mergeSelfServiceRef.current(heal);
        mergeSelfServiceWithDetailsRef.current(heal);
      });
    } catch {
      // Silencioso salvo el error de carga estándar: la reconciliación es best-effort y NO debe
      // romper el realtime ni spamear toasts. Reusa el mensaje del listado (salvo disparo WS silente).
      if (!silentError) toast.error(MSG_LIST_ERROR);
    } finally {
      inFlightRef.current = false;
    }
    // Todas las deps se leen vía ref → función estable (deps []); no se recrea ni re-registra los
    // listeners en cada render.
  }, []);

  const reconcileRef = useRef(reconcile);
  reconcileRef.current = reconcile;

  // Refs para que el listener WS (registrado una sola vez) lea SIEMPRE las funciones de sub/unsub
  // más recientes sin re-registrarse en cada render (subscribeEvent/unsubscribeEvent del context no
  // están memoizadas). Mismo patrón de ref que reconcileRef.
  const subscribeEventRef = useRef(subscribeEvent);
  subscribeEventRef.current = subscribeEvent;
  const unsubscribeEventRef = useRef(unsubscribeEvent);
  unsubscribeEventRef.current = unsubscribeEvent;

  // Trigger 1: (re)conexión del socket. El epoch arranca en 0; el primer `onopen` lo lleva a 1.
  // Ignoramos el 0 inicial (no hay conexión todavía) para no duplicar el fetch inicial que ya hace
  // useGetOrdersWithStatus al montar el Kanban.
  useEffect(() => {
    if (connectionEpoch === 0) return;
    reconcileRef.current();
  }, [connectionEpoch]);

  // Trigger 2: foco / visibilidad. Cubre laptop dormida / pestaña en background al dispararse el
  // auto_fire. Registrado una sola vez; lee el valor fresco vía reconcileRef.
  useEffect(() => {
    const onFocus = () => reconcileRef.current();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') reconcileRef.current();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // Trigger 3 (T4): CUALQUIER evento WS entrante puede señalar que una asociación de mesa recién se
  // materializó en el backend. Reconciliamos con debounce trailing de 12s (una sola llamada tras la
  // ráfaga) + techo de 18s (maxWait) para que el tráfico WS sostenido no mate de hambre el sanado.
  // El disparo se GATEA: solo corre si el módulo SELF_SERVICE está activo Y hay en el store un pedido
  // de mesa suelto que sanar → evita el GET completo del listado cada ~12s cuando no hay nada que
  // hacer (la mayoría de las ráfagas). Registrado una sola vez; lee reconcile/sub/unsub/módulo vía refs.
  useEffect(() => {
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    let maxWaitTimer: ReturnType<typeof setTimeout> | null = null;

    const fire = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      if (maxWaitTimer) clearTimeout(maxWaitTimer);
      debounceTimer = null;
      maxWaitTimer = null;
      // Gate (leído en el momento del disparo, no del evento): módulo activo (fresco vía ref) y algún
      // pedido de mesa suelto en el store. Sin candidatos → no toca la red.
      if (!hasModuleRef.current('SELF_SERVICE')) return;
      if (!hasPendingSelfServiceHeal(ordersRef.current)) return;
      reconcileRef.current({ silentError: true });
    };

    const onAnyWsEvent = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(fire, SELF_SERVICE_BACKSTOP_DEBOUNCE_MS);
      // maxWait: se arma una sola vez por ráfaga y NO se reinicia con cada evento → garantiza un
      // disparo a más tardar a los 18s aunque los eventos sigan llegando cada pocos segundos.
      if (!maxWaitTimer) maxWaitTimer = setTimeout(fire, SELF_SERVICE_BACKSTOP_MAX_WAIT_MS);
    };
    const events = Object.values(SOCKET_EVENTS);
    events.forEach((ev) => subscribeEventRef.current(ev, onAnyWsEvent));
    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      if (maxWaitTimer) clearTimeout(maxWaitTimer);
      events.forEach((ev) => unsubscribeEventRef.current(ev, onAnyWsEvent));
    };
  }, []);

  return { reconcile };
};

export default useReconcileActiveOrders;
